import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { transcribeAudio, speakText } from "@/utils/grokSpeech";

type Candidate = Tables<"candidates">;

interface Question {
  id: number;
  text: string;
}

export default function VoiceInterviewPage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const { toast } = useToast();
  const [mainQuestions, setMainQuestions] = useState<Question[]>([]);
  const [currentMain, setCurrentMain] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [transcript, setTranscript] = useState("");
  const [history, setHistory] = useState<{ question: string; answer: string }[]>([]);
  const [started, setStarted] = useState(false);
  const [awaitingFinal, setAwaitingFinal] = useState(false);
  const [questionAudio, setQuestionAudio] = useState<HTMLAudioElement | null>(
    null
  );
  const [playing, setPlaying] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [finalSubmitting, setFinalSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const recorder = useAudioRecorder();

  const { data: candidate } = useQuery({
    queryKey: ["candidate", candidateId],
    queryFn: async () => {
      if (!candidateId) throw new Error("Candidate ID is required");
      const { data, error } = await supabase
        .from("candidates")
        .select("*")
        .eq("id", candidateId)
        .single();
      if (error) throw error;
      return data as Candidate;
    },
    enabled: !!candidateId,
  });

  const generateQuestions = async () => {
    if (!candidateId || loadingQuestions) return;
    setLoadingQuestions(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "generate-voice-questions",
        { body: { candidateId } }
      );
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      const qs = (data.questions as string[]).map((q: string, idx: number) => ({
        id: idx + 1,
        text: q,
      }));
      setMainQuestions(qs);
      setCurrentMain(0);
      setCurrentQuestion(qs[0]?.text ?? null);
      setHistory([]);
      setTranscript("");
      setStarted(true);
      setAwaitingFinal(false);
      setCompleted(false);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handlePlayPause = async () => {
    if (!currentQuestion || loadingAudio) return;
    if (!questionAudio) {
      setLoadingAudio(true);
      try {
        const audio = await speakText(currentQuestion);
        setQuestionAudio(audio);
        audio.addEventListener("ended", () => {
          setPlaying(false);
          setQuestionAudio(null);
        });
        await audio.play();
        setPlaying(true);
      } finally {
        setLoadingAudio(false);
      }
    } else if (playing) {
      questionAudio.pause();
      setPlaying(false);
    } else {
      await questionAudio.play();
      setPlaying(true);
    }
  };

  const handleStopAudio = () => {
    if (questionAudio) {
      questionAudio.pause();
      questionAudio.currentTime = 0;
      setPlaying(false);
    }
  };

  useEffect(() => {
    if (questionAudio) {
      questionAudio.pause();
      questionAudio.currentTime = 0;
      setQuestionAudio(null);
      setPlaying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion]);

  const handleStopRecord = async () => {
    if (transcribing) return;
    setTranscribing(true);
    try {
      const blob = await recorder.stop();
      if (blob) {
        const text = await transcribeAudio(blob);
        setTranscript((prev) => (prev ? `${prev} ${text}` : text));
      }
    } finally {
      setTranscribing(false);
    }
  };

  const handleSubmit = async () => {
    if (
      !candidateId ||
      !currentQuestion ||
      transcript.trim() === "" ||
      submitting
    )
      return;
    setSubmitting(true);
    const newHistory = [
      ...history,
      { question: currentQuestion, answer: transcript },
    ];
    const { data, error } = await supabase.functions.invoke(
      "continue-voice-interview",
      { body: { candidateId, history: newHistory } }
    );
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setSubmitting(false);
      return;
    }
    setTranscript("");
    if (data.nextQuestion) {
      setHistory(newHistory);
      setCurrentQuestion(data.nextQuestion as string);
    } else {
      setHistory(newHistory);
      setCurrentQuestion(null);
      setAwaitingFinal(true);
    }
    setSubmitting(false);
  };

  const handleFinalSubmit = () => {
    if (finalSubmitting) return;
    setFinalSubmitting(true);
    if (currentMain < mainQuestions.length - 1) {
      const nextMain = currentMain + 1;
      setCurrentMain(nextMain);
      setCurrentQuestion(mainQuestions[nextMain].text);
      setHistory([]);
      setTranscript("");
      setAwaitingFinal(false);
      toast({
        title: `Proceeding to Question ${nextMain + 1}`,
      });
      setFinalSubmitting(false);
    } else {
      setCompleted(true);
      toast({ title: "Interview complete", description: "Thank you" });
      setFinalSubmitting(false);
    }
  };

  if (!candidate) return <div>Loading...</div>;

  const mainNumber = currentMain + 1;
  const followUpLabel = history.length > 0 ? ` - Follow-up ${history.length}` : "";

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Voice Interview - {candidate.full_name}</h1>
      {completed ? (
        <Card>
          <CardHeader>
            <CardTitle>Thank you for completing the interview.</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Your responses have been recorded.
          </CardContent>
        </Card>
      ) : (
        <>
          <Button
            onClick={generateQuestions}
            disabled={started || loadingQuestions}
          >
            Start Interview
          </Button>

          {history.map((item, idx) => (
            <Card key={idx}>
              <CardHeader>
                <CardTitle>
                  {`Question ${mainNumber}${idx > 0 ? ` - Follow-up ${idx}` : ""}`}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">Question</p>
                <p>{item.question}</p>
                <p className="font-medium mt-2">Your Answer</p>
                <p>{item.answer}</p>
              </CardContent>
            </Card>
          ))}

          {currentQuestion && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {`Question ${mainNumber}${followUpLabel}: ${currentQuestion}`}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  {!recorder.recording ? (
                    <Button
                      onClick={recorder.start}
                      disabled={transcribing || submitting}
                    >
                      Record Answer
                    </Button>
                  ) : (
                    <Button
                      onClick={handleStopRecord}
                      disabled={transcribing}
                    >
                      Stop Recording
                    </Button>
                  )}
                  {recorder.recording && (
                    <span className="text-red-500 text-sm">Recording...</span>
                  )}
                  <Button
                    variant="outline"
                    onClick={handlePlayPause}
                    disabled={loadingAudio || recorder.recording}
                  >
                    {playing ? "Pause" : "Play Question"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleStopAudio}
                    disabled={!questionAudio || recorder.recording}
                  >
                    Stop
                  </Button>
                </div>
                <Textarea
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Transcribed answer"
                  required
                />
                <Button
                  onClick={handleSubmit}
                  disabled={
                    submitting || transcribing || transcript.trim() === ""
                  }
                >
                  Submit Answer
                </Button>
              </CardContent>
            </Card>
          )}

          {awaitingFinal && (
            <Card>
              <CardHeader>
                <CardTitle>End of Question {mainNumber}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Review the conversation above and continue.
                </p>
                <Button
                  onClick={handleFinalSubmit}
                  disabled={finalSubmitting}
                >
                  Submit and continue
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
